import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import Button from '../components/common/Button'
import Spinner from '../components/common/Spinner'
import { LessonDonePill } from '../components/learning/LessonDone'
import {
  isEmptyPath,
  isPremiumRequired,
  lessonState,
  pathStages,
  stageProgress,
  stageState,
  starSlots,
} from '../lib/arcade'
import { clock } from '../lib/clock'
import { lessonCardLook } from '../lib/lessonDone'
import { faDigits } from '../lib/numerals'
import { classifyFailure } from '../services/api'
import { fetchPath } from '../services/arcade'
import { useToastStore } from '../store/toastStore'

function StarGlyph({ filled }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={filled ? 'text-accent' : 'text-ink-3'}
    >
      <path d="M12 3.6l2.6 5.3 5.8.85-4.2 4.1 1 5.75L12 16.9l-5.2 2.7 1-5.75-4.2-4.1 5.8-.85z" />
    </svg>
  )
}

function LockGlyph({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4.5" y="10.5" width="15" height="10" rx="2.5" />
      <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
    </svg>
  )
}

function CheckGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  )
}

/** The three (or however many the panel configures) star slots of one lesson, empty ones included. */
export function StarRow({ lesson, thresholds }) {
  const { t } = useTranslation('arcade')
  const slots = starSlots(lesson, thresholds)
  const earned = slots.filter(Boolean).length
  return (
    <span className="flex items-center gap-0.5" role="img" aria-label={t('lesson.stars', { earned: faDigits(earned), total: faDigits(slots.length) })}>
      {slots.map((filled, index) => (
        <StarGlyph key={index} filled={filled} />
      ))}
    </span>
  )
}

const ROW_LOOK = {
  // The current lesson is the one thing on this screen that must be
  // findable without reading: it keeps full contrast and a brand ring.
  current: 'border border-brand-300 bg-brand-50 ring-1 ring-brand-200',
  // A finished lesson's card turns green — the same look as the flat
  // catalogue and home (components/learning/LessonDone.jsx).
  completed: lessonCardLook(true),
  open: 'card-soft',
  // Dimmed, not hidden — a locked lesson still shows what is coming.
  locked: 'card-soft bg-surface-2 opacity-60',
}

/**
 * One lesson on the path.
 *
 * A locked row is deliberately a `<button>`, not a dead `<div>` and not a
 * `<Link>`: the Android path answers a tap on it with «اول درس قبلی را تمام
 * کن» rather than doing nothing, and a control that says something has to
 * be focusable and announce itself as disabled-ish. `aria-disabled` rather
 * than `disabled` for exactly that reason — a `disabled` button cannot be
 * focused, so the explanation would be unreachable by keyboard.
 */
export function LessonRow({ lesson, thresholds, number, onLocked }) {
  const { t } = useTranslation('arcade')
  const state = lessonState(lesson)
  const locked = state === 'locked'
  const score = lesson.lessonScore === null || lesson.lessonScore === undefined ? null : Math.round(lesson.lessonScore)

  const body = (
    <>
      <span
        className={`grid size-9 shrink-0 place-items-center rounded-full text-[13px] font-bold ${
          state === 'current'
            ? 'bg-brand-600 text-white'
            : state === 'completed'
              ? 'bg-good text-on-ink'
              : locked
                ? 'bg-surface-3 text-ink-3'
                : 'bg-brand-50 text-brand-700'
        }`}
        aria-hidden="true"
      >
        {locked ? <LockGlyph size={15} /> : state === 'completed' ? <CheckGlyph /> : faDigits(number)}
      </span>

      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span dir="ltr" className="lesson-text block truncate text-right text-[15px] font-medium text-ink">
          {lesson.titleEn}
        </span>
        {lesson.titleFa && <span className="truncate text-xs text-ink-2">{lesson.titleFa}</span>}
        <span className="mt-0.5 flex items-center gap-2">
          <StarRow lesson={lesson} thresholds={thresholds} />
          <span className="tnum text-[11px] text-ink-3" dir="ltr">
            {clock(lesson.durationSeconds)}
          </span>
          {score !== null && (
            <span className="tnum text-[11px] font-semibold text-ink-3">
              {faDigits(score)} {t('lesson.score')}
            </span>
          )}
        </span>
      </span>

      {state === 'current' && <span className="shrink-0 rounded-md bg-brand-600 px-2 py-[3px] text-[10.5px] font-semibold text-white">{t('lesson.current')}</span>}
      {state === 'completed' && <LessonDonePill />}
      {locked && <span className="shrink-0 text-[10.5px] font-semibold text-ink-3">{t('lesson.locked')}</span>}
    </>
  )

  const shell = `flex min-h-[72px] w-full items-center gap-3 rounded-2xl p-3 text-start transition ${ROW_LOOK[state]}`

  return (
    <li>
      {locked ? (
        <button type="button" aria-disabled="true" data-lesson-state={state} onClick={() => onLocked?.(lesson)} className={shell}>
          {body}
        </button>
      ) : (
        <Link to={`/lesson/${lesson.id}`} data-lesson-state={state} className={`${shell} ${state === 'completed' ? 'hover:brightness-[0.98]' : 'hover:bg-surface-2'}`}>
          {body}
        </Link>
      )}
    </li>
  )
}

/**
 * One stage: its header — number, both titles, how many lessons are done,
 * how many stars it holds — then its lessons. A locked stage dims the whole
 * panel and says why; its lessons stay listed, each locked in its own right
 * (the backend locks every lesson of a locked stage), so the learner can
 * see what the stage contains before they get there.
 */
export function StageCard({ stage, index, thresholds, current, onLocked }) {
  const { t } = useTranslation('arcade')
  const state = stageState(stage, current)
  const progress = stageProgress(stage)

  return (
    <section
      data-stage-state={state}
      className={`card-soft relative p-4 ${
        state === 'locked' ? 'bg-surface-2/60' : state === 'current' ? 'ring-1 ring-brand-200' : ''
      }`}
    >
      <header className={`mb-3 flex items-start gap-3 ${state === 'locked' ? 'opacity-70' : ''}`}>
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="flex items-center gap-2">
            <span className="text-[11px] font-semibold text-ink-3">{t('stage.index', { number: faDigits(index + 1) })}</span>
            {state === 'locked' && (
              <span className="inline-flex items-center gap-1 rounded-md bg-surface-3 px-1.5 py-[2px] text-[10.5px] font-semibold text-ink-3">
                <LockGlyph size={12} />
                {t('stage.locked')}
              </span>
            )}
            {state === 'completed' && <span className="text-[10.5px] font-semibold text-good">{t('lesson.completed')}</span>}
          </span>
          <h2 className="truncate text-[17px] font-bold text-ink">{stage.titleFa}</h2>
          <span dir="ltr" className="lesson-text truncate text-right text-[12.5px] text-ink-2">
            {stage.titleEn}
          </span>
        </span>
        <span className="flex shrink-0 flex-col items-end gap-1">
          <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-accent-ink">
            <StarGlyph filled />
            <span className="tnum">{t('stage.stars', { earned: faDigits(stage.starsEarned ?? 0), available: faDigits(stage.starsAvailable ?? 0) })}</span>
          </span>
          <span className="tnum text-[11px] text-ink-3">{t('stage.progress', { done: faDigits(progress.done), total: faDigits(progress.total) })}</span>
        </span>
      </header>

      {state === 'locked' && <p className="mb-3 text-[11.5px] leading-[1.9] text-ink-3">{t('stage.lockedHint')}</p>}

      {progress.total === 0 ? (
        <p className="py-3 text-center text-[12.5px] text-ink-3">{t('stage.empty')}</p>
      ) : (
        <ul className="space-y-2">
          {(stage.lessons ?? []).map((lesson, lessonIndex) => (
            <LessonRow key={lesson.id} lesson={lesson} thresholds={thresholds} number={lessonIndex + 1} onLocked={onLocked} />
          ))}
        </ul>
      )}
    </section>
  )
}

/**
 * The path itself: the stages stacked vertically with a connector between
 * them, so it reads as one route rather than a list of cards. Handed the
 * whole `ArcadePathDto`, which makes it renderable from a fixture in the
 * smoke test without a server.
 */
export function PathView({ path, onLocked }) {
  const { t } = useTranslation('arcade')
  const stages = pathStages(path)

  if (isEmptyPath(path)) {
    return (
      <div className="flex flex-col items-center gap-3 card-soft px-6 py-10 text-center">
        <p className="text-[17px] font-bold text-ink">{t('empty.title')}</p>
        <p className="max-w-[260px] text-[12.5px] leading-[1.9] text-ink-2">{t('empty.body')}</p>
        <Link to="/lessons/all" className="text-[12.5px] font-semibold text-brand-600 hover:underline">
          {t('allLessons')}
        </Link>
      </div>
    )
  }

  return (
    <ol className="space-y-0">
      {stages.map((stage, index) => (
        <li key={stage.id}>
          {index > 0 && <span aria-hidden="true" className="mx-auto block h-5 w-[3px] rounded-full bg-line" />}
          <StageCard stage={stage} index={index} thresholds={path.starThresholds} current={path.current} onLocked={onLocked} />
        </li>
      ))}
    </ol>
  )
}

/**
 * The 403 (Q22): the staged library is the premium plan's, the normal plan
 * keeps the daily calendar. `arcade.requirePremium` is off today, so this
 * card is what the app will show the day the client turns it on — a door to
 * Tooti+ and, next to it, the flat catalogue, which is not premium and
 * keeps working either way.
 */
export function PremiumCard() {
  const { t } = useTranslation('arcade')
  return (
    <section className="flex flex-col gap-3 rounded-2xl border border-line bg-accent-tint p-5">
      <span className="inline-flex w-fit items-center rounded-md bg-accent px-2 py-[3px] text-[11px] font-bold text-on-accent">{t('premium.badge')}</span>
      <h2 className="text-[17px] font-bold text-ink">{t('premium.title')}</h2>
      <p className="text-[12.5px] leading-[1.9] text-ink-2">{t('premium.body')}</p>
      <div className="flex flex-wrap items-center gap-2">
        <Link
          to="/paywall"
          className="inline-flex items-center justify-center rounded-xl bg-brand-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-brand-700"
        >
          {t('premium.cta')}
        </Link>
        <Link to="/lessons/all" className="px-2 text-[12.5px] font-semibold text-brand-700 hover:underline">
          {t('premium.secondary')}
        </Link>
      </div>
    </section>
  )
}

/**
 * The library tab (V7-w): the staged path of the learner's own level,
 * ahead of the flat catalogue, which stays one tap away as «همهٔ درس‌ها»
 * (`/lessons/all`) and is not going anywhere.
 *
 * Everything the screen shows comes from one `arcade_path` read, re-asked
 * on every mount — stars and locks are computed server-side on read, so a
 * lesson finished a moment ago is already reflected when the learner comes
 * back here from it. Design tokens are today's (the capsule look is V8).
 */
export default function ArcadePath() {
  const { t } = useTranslation('arcade')
  const showToast = useToastStore((s) => s.show)
  const [path, setPath] = useState(null)
  const [error, setError] = useState(null)
  const [reload, setReload] = useState(0)

  useEffect(() => {
    let cancelled = false
    setError(null)
    fetchPath()
      .then((data) => {
        if (!cancelled) setPath(data)
      })
      .catch((e) => {
        if (!cancelled) setError(e)
      })
    return () => {
      cancelled = true
    }
  }, [reload])

  const onLocked = () => showToast(t('lesson.lockedToast'))

  return (
    <div className="mx-auto max-w-md space-y-[13px]">
      <header className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <h1 className="truncate text-[23px] font-bold tracking-[-0.3px] text-ink">{t('title')}</h1>
          <p className="text-[13px] text-ink-2">{path?.level ? t(`levels.${path.level}`) : t('subtitle')}</p>
        </div>
        <Link to="/lessons/all" className="shrink-0 pt-1 text-[12.5px] font-semibold text-brand-600 hover:underline">
          {t('allLessons')}
        </Link>
      </header>

      {isPremiumRequired(error) ? (
        <PremiumCard />
      ) : error && !path ? (
        <div className="space-y-3">
          <div role="status" className={`rounded-xl p-3 text-sm ${classifyFailure(error) === 'offline' ? 'bg-warn-tint text-warn-ink' : 'bg-bad-tint text-bad-ink'}`}>
            {classifyFailure(error) === 'offline' ? t('loadError.offline') : t('loadError.generic')}
          </div>
          <Button variant="secondary" className="w-full" onClick={() => setReload((n) => n + 1)}>
            {t('loadError.retry')}
          </Button>
        </div>
      ) : !path ? (
        <Spinner label={t('loading')} />
      ) : (
        <PathView path={path} onLocked={onLocked} />
      )}
    </div>
  )
}
